import React, { useEffect, useState } from 'react'
import { MessageSquarePlus, RefreshCw } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { BriefPanel, UnavailablePanel } from '../types'
import { useStartZephChat, type ZephChatSeed } from '../hooks/useStartZephChat'
import { getTimeOfDay, GREETING } from '../lib/timeOfDay'
import GreetingBackdrop from './GreetingBackdrop'

interface ZephBriefProps {
  panel: BriefPanel | UnavailablePanel
  onRegenerate: () => void
  regenerating: boolean
}

// Reads the same `user` localStorage entry Sidebar already reads (set on
// login/register — see features/auth/hooks/useAuth.tsx) rather than adding a new
// fetch for something the app already has available client-side.
function readFirstName(): string {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return 'there'
    const parsed = JSON.parse(raw) as { name?: string }
    const name = parsed?.name?.trim()
    return name ? (name.split(' ')[0] ?? name) : 'there'
  } catch {
    return 'there'
  }
}

// The brief is the reason to open the app, so it can be talked to: this hands Zeph
// its own briefing back as context rather than starting a cold chat.
function briefChatSeed(body: string): ZephChatSeed {
  return {
    // Per day, so re-opening the brief conversation reopens the same one.
    seedKey: `zeph-brief:${new Date().toISOString().slice(0, 10)}`,
    title: "Today's brief",
    briefing: [
      `[[zeph:brief]] The user just clicked through from the daily brief you wrote on their dashboard. ` +
        `Open the conversation yourself. Expand on the brief below — the detail behind it and what you would do first. ` +
        `Keep it conversational but tight, and end with one clear recommended next step.`,
      ``,
      `The brief you wrote:`,
      body,
    ].join('\n'),
  }
}

// The greeting banner and its time-of-day landscape are the shipped GreetingPanel
// artwork, kept and repurposed: the brief now sits on that canvas instead of a task
// list. The gradient scrim is what keeps white text legible over any sky.
const ZephBrief: React.FC<ZephBriefProps> = ({ panel, onRegenerate, regenerating }) => {
  const { currentTheme: t } = useTheme()
  const startZephChat = useStartZephChat()
  const [firstName, setFirstName] = useState('there')

  useEffect(() => {
    setFirstName(readFirstName())
  }, [])

  const period = getTimeOfDay()
  const { text: greetingText, Icon } = GREETING[period]

  const body =
    panel.status === 'ready'
      ? panel.body
      : "I couldn't put a briefing together just now — everything below is still up to date."

  const stamp =
    panel.status === 'ready'
      ? new Date(panel.generatedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      : null

  return (
    <section
      className="overflow-hidden rounded-2xl border"
      style={{ borderColor: t.border, backgroundColor: t.secondary, boxShadow: t.cardShadow }}
      aria-label="Your brief from Zeph"
    >
      <div className="relative">
        <div className="absolute inset-0">
          <GreetingBackdrop period={period} />
        </div>
        {/* Darkens the lower half so the brief reads on any sky, in either theme. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.42) 45%, rgba(0,0,0,0.72) 100%)',
          }}
        />

        <div className="relative p-5 md:p-7 flex flex-col gap-4">
          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-xl shrink-0 self-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.22)' }}
            >
              <Icon size={18} color="#FFFFFF" />
            </span>
            <h1
              className="text-xl md:text-2xl font-bold text-white"
              style={{ textShadow: '0 1px 10px rgba(0,0,0,0.55)' }}
            >
              {greetingText}, {firstName}
            </h1>
            {stamp && (
              <span
                className="text-[11px] font-semibold uppercase tracking-wider"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                Brief · {stamp}
              </span>
            )}
          </div>

          <p
            className="max-w-[62ch] text-sm md:text-[15px] leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 8px rgba(0,0,0,0.45)' }}
          >
            {body}
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => startZephChat(briefChatSeed(body))}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-transform duration-200 hover:-translate-y-px active:translate-y-0"
              style={{ backgroundColor: '#FFFFFF', color: t.accent }}
            >
              <MessageSquarePlus size={14} />
              Ask Zeph about this
            </button>
            <button
              type="button"
              onClick={onRegenerate}
              disabled={regenerating}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-opacity duration-200 hover:opacity-100 disabled:cursor-not-allowed"
              style={{ color: 'rgba(255,255,255,0.72)' }}
            >
              <RefreshCw size={13} className={regenerating ? 'animate-spin' : ''} />
              {regenerating ? 'Rewriting…' : 'Regenerate'}
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default React.memo(ZephBrief)
